import type {
  BdVirtualClinicAdmissionReason,
} from '@bindoc-projects/virtual-clinic-domain-lib';
import {
  BD_VIRTUAL_CLINIC_ADMISSION_REASON_DISPLAY_NAMES,
} from '@bindoc-projects/virtual-clinic-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';
import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';

export const BD_ADMISSION_REASON_CONDITION_ID = 'admissionReason';
export const FilterConditionAdmissionReasonPluginType: FilterConditionPluginSpecification<
  BdVirtualClinicAdmissionReason[],
  FilterConditionAdmissionReasonDto,
  BdAdmissionReasonCondition
> = {
  type: 'admissionReasons',
  category: 'case',
  displayName: { de: 'Aufnahmegrund' },
  sortingIndex: 8,
  occurrence: 'single',
  fromObject(
    obj: FilterConditionAdmissionReasonDto,
  ): FilterConditionPluginImpl<BdVirtualClinicAdmissionReason[]> {
    return new FilterConditionAdmissionReasonPlugin(
        obj.admissionReasons as BdVirtualClinicAdmissionReason[],
      );
  },
  fromCondition(
    jsonObj: BdAdmissionReasonCondition,
  ): FilterConditionPluginImpl<BdVirtualClinicAdmissionReason[]> {
    return new FilterConditionAdmissionReasonPlugin(jsonObj.includes ?? []);
  },
  newInstance(): FilterConditionPluginImpl<BdVirtualClinicAdmissionReason[]> {
    return new FilterConditionAdmissionReasonPlugin([]);
  },
};

export class FilterConditionAdmissionReasonPlugin extends FilterConditionPluginImpl<
  BdVirtualClinicAdmissionReason[],
  FilterConditionAdmissionReasonDto
> {
  static override Specification: FilterConditionPluginSpecification<
    BdVirtualClinicAdmissionReason[],
    FilterConditionAdmissionReasonDto,
    BdAdmissionReasonCondition
  > = FilterConditionAdmissionReasonPluginType;

  constructor(private admissionReasons: BdVirtualClinicAdmissionReason[]) {
    super(FilterConditionAdmissionReasonPluginType.type);
  }

  clone(): FilterConditionAdmissionReasonPlugin {
    return new FilterConditionAdmissionReasonPlugin([...this.admissionReasons]);
  }

  override equals(other: FilterConditionAdmissionReasonPlugin): boolean {
    return (
      super.equals(other) &&
      BdArrayUtil.equals(this.admissionReasons, other.admissionReasons)
    );
  }

  override validationToText(): string {
    if (this.admissionReasons.length === 0) {
      return 'Mindestens einen Aufnahmegrund auswählen';
    } else {
      return '';
    }
  }

  toCondition(): BdCondition | null {
    return new BdAdmissionReasonCondition(this.admissionReasons, []);
  }

  toJSON(): FilterConditionAdmissionReasonDto | null {
    return {
      type: this.type,
      admissionReasons: this.admissionReasons,
    };
  }

  override toDetailedText(): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const items = this.admissionReasons.map((reason) => {
      const displayName =
        BD_VIRTUAL_CLINIC_ADMISSION_REASON_DISPLAY_NAMES[reason];
      return displayName ? `${reason} - ${displayName}` : reason;
    });
    return { title, items };
  }

  toText(): string | null {
    return this.admissionReasons.length === 1
      ? 'ein Aufnahmegrund'
      : `${this.admissionReasons.length} Aufnahmegründe`;
  }

  toValue(): BdVirtualClinicAdmissionReason[] {
    return this.admissionReasons;
  }
}

class BdAdmissionReasonCondition extends BdContentCondition<BdVirtualClinicAdmissionReason> {
  constructor(
    includes: BdVirtualClinicAdmissionReason[],
    excludes: BdVirtualClinicAdmissionReason[],
  ) {
    super(BD_ADMISSION_REASON_CONDITION_ID, includes, excludes);
  }
}

interface FilterConditionAdmissionReasonDto {
  type: string;
  admissionReasons: string[];
}
